import * as React from "react";
import { Dayjs } from "dayjs";
import dayJsGenerateConfig from "rc-picker/lib/generate/dayjs";
import { RangeValue as RangeValueRcPicker } from "rc-picker/lib/interface";
import generatePicker, {
	PickerProps,
	RangePickerProps as RangePickerPropsUi,
} from "antd/lib/date-picker/generatePicker";

import "./date-picker.scss";

export type DatePickerAbsProps = PickerProps<Dayjs>;

export type RangeValue<DateType> = RangeValueRcPicker<DateType>;

export type RangePickerProps = RangePickerPropsUi<Dayjs> & {
	shouldAdjustRange?: boolean;
	resetInitialValueOnOpen?: boolean;
};

const CustomDatePicker = generatePicker<Dayjs>(dayJsGenerateConfig);

export function DatePicker({ ...props }: DatePickerAbsProps) {
	return <CustomDatePicker {...props} />;
}

function RangePicker({
	value,
	onChange,
	onCalendarChange: onCalendarChangeProp,
	shouldAdjustRange = true,
	resetInitialValueOnOpen = false,
	...props
}: RangePickerProps) {
	const [dates, setDates] = React.useState<[Dayjs | null, Dayjs | null]>(
		value ?? [null, null],
	);

	return (
		<CustomDatePicker.RangePicker
			value={dates}
			onOpenChange={open => {
				if (open && resetInitialValueOnOpen) {
					return setDates([null, null]);
				}
			}}
			onCalendarChange={(period, formatString, info) => {
				/**
				 * Use the provided onCalendarChangeProp callback if available,
				 * otherwise fall back to the onChange callback
				 */
				const changeFn = onCalendarChangeProp ?? onChange;

				/**
				 * If no period is provided, reset the dates to null
				 * and call the change function with null values if it exists
				 */
				if (!period) {
					setDates([null, null]);
					if (!changeFn) return;
					changeFn(null, formatString, info);
					return;
				}

				/**
				 * validation and casting is necessary because changeFn expects
				 * a tuple of two values, instead of an array
				 * */
				// eslint-disable-next-line @typescript-eslint/no-magic-numbers
				if (period.length !== 2 || !period.every(Boolean)) return;

				setDates(period);

				if (!changeFn) return;

				const asserted = period as [Dayjs, Dayjs];
				const normalized = shouldAdjustRange
					? (asserted.map((date, i) => {
							date = i === 0 ? date.startOf("day") : date.endOf("day");
							return date;
					  }) as [Dayjs, Dayjs])
					: asserted;

				changeFn(normalized, formatString, info);
			}}
			{...props}
		/>
	);
}

DatePicker.RangePicker = RangePicker;
DatePicker.TimePicker = CustomDatePicker.TimePicker;
DatePicker.WeekPicker = CustomDatePicker.WeekPicker;
DatePicker.MonthPicker = CustomDatePicker.MonthPicker;
DatePicker.QuarterPicker = CustomDatePicker.QuarterPicker;
DatePicker.YearPicker = CustomDatePicker.YearPicker;
